import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: '8px',
    maxWidth: '480px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    backgroundColor: '#fafafa',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#000000',
  padding: '24px 24px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  backgroundColor: '#fafafa',
});

const StyledDialogContent = styled(DialogContent)({
  fontFamily: "'DM Sans', sans-serif",
  padding: '0 24px 24px',
  backgroundColor: '#fafafa',
});

const StyledDialogContentText = styled(DialogContentText)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1rem',
  color: '#212121',
  lineHeight: 1.6,
  fontWeight: 500,
});

const StyledDialogActions = styled(DialogActions)({
  padding: '16px 24px 24px',
  gap: '12px',
  backgroundColor: '#fafafa',
});

const ConfirmButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
  color: 'white',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  fontSize: '0.9375rem',
  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #d32f2f 0%, #c2185b 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)',
  },
});

const CancelButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: '#ffffff',
  color: '#000000',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  fontSize: '0.9375rem',
  border: '2px solid #bdbdbd',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: '#e0e0e0',
    borderColor: '#1a73e8',
    color: '#1a73e8',
    transform: 'translateY(-2px)',
  },
});

const IconWrapper = styled(Box)({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
  marginBottom: '16px',
});

export default function ConfirmationModal({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) {
  return (
    <StyledDialog
      open={open}
      onClose={onCancel}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      disableScrollLock
    >
      <StyledDialogTitle id="confirmation-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <IconWrapper>
            <WarningAmberIcon sx={{ fontSize: 28, color: '#d32f2f' }} />
          </IconWrapper>
          <span>{title}</span>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            color: '#000000',
            '&:hover': {
              backgroundColor: '#e0e0e0',
              color: '#000000',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <StyledDialogContentText id="confirmation-dialog-description">
          {message}
        </StyledDialogContentText>
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onCancel}>
          {cancelText}
        </CancelButton>
        <ConfirmButton onClick={onConfirm} autoFocus>
          {confirmText}
        </ConfirmButton>
      </StyledDialogActions>
    </StyledDialog>
  );
}
